// API client hook for all fetch operations

const BASE = '/api';

// The API refuses a person's requests past a per-minute limit with a 429 and a
// Retry-After (pen test AD25996 / AD26341, AD14; the limit is in wrangler.toml).
// It is set far above what anybody does by hand, so the one way to meet it is a
// bulk action sending a request per row. Waiting and retrying turns that into
// a slower action rather than a half-applied one.
const RATE_LIMIT_RETRIES = 3;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// ── A REFUSAL FROM CLOUDFLARE, NOT FROM OPS ─────────────────────────────────
//
// Every error the Worker returns is JSON with a message in it. A reply that is
// not JSON never reached the Worker: Cloudflare's bot protection answers a
// request it scores as a likely bot with a challenge page (403, text/html,
// cf-mitigated: challenge), which a fetch cannot solve. That used to surface as
// a bare "Request failed", which read as the app being broken, and somebody
// reporting it could not say which of the two had happened.
//
// A challenge is passed by loading a page, so reloading is the advice, and
// nothing was saved because the app never saw the request.
export const CHALLENGE_MESSAGE =
  "Cloudflare's security check stopped that request before it reached Ops, so nothing was saved. "
  + 'Reload the page and try again.';

async function errorFrom(res) {
  if (res.headers.get('cf-mitigated') === 'challenge') {
    return { error: CHALLENGE_MESSAGE, challenged: true };
  }
  return res.json().catch(() => ({
    error: `Ops did not answer that request (HTTP ${res.status}), so it may not have been saved. Try again in a moment.`,
  }));
}

async function request(path, options = {}) {
  let res;
  for (let attempt = 0; ; attempt++) {
    res = await fetch(`${BASE}/${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      body: options.body ? JSON.stringify(options.body) : undefined,
    });
    if (res.status !== 429 || attempt >= RATE_LIMIT_RETRIES) break;
    const wait = Math.min(Number(res.headers.get('Retry-After')) || 60, 60);
    await sleep(wait * 1000);
  }

  if (res.status === 401) {
    // A 401 used to redirect straight to /auth/login. Cloudflare Access then
    // re-authenticates silently and drops you back at the app, which looks like
    // the page randomly refreshing — and takes whatever was typed with it.
    //
    // The calls it happened on were the slow ones: an AI extract reading a PDF
    // takes 10-60 seconds, which is long enough for a session cache entry to
    // lapse mid-request even though the session itself is fine.
    //
    // So a single 401 is no longer treated as proof. It is checked once against
    // a cheap endpoint, and only a second 401 redirects. A transient one becomes
    // an error the caller can show and the person can retry, with their work
    // still on screen.
    if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth/')) {
      // Deliberately a raw fetch, not request(): going back through here would
      // recurse on its own 401.
      const alive = await fetch(`${BASE}/people/me`, { headers: { 'Content-Type': 'application/json' } })
        .then(r => r.ok)
        .catch(() => false);
      if (!alive) {
        window.location.href = '/auth/login';
        const gone = new Error('Your session has expired. Redirecting to sign in…');
        gone.status = 401;
        throw gone;
      }
    }
    const e = new Error('That request was not authorised, but you are still signed in. Please try again.');
    e.status = 401;
    e.transient = true;
    throw e;
  }

  if (!res.ok) {
    const errData = await errorFrom(res);
    const e = new Error(errData.error || `HTTP ${res.status}`);
    e.data = errData;   // preserve full payload (e.g. { error, missing } from validation)
    e.status = res.status;
    throw e;
  }

  // Something was changed, so a sidebar badge counting it may be out of date:
  // approving a request should take its badge down now, not on the next poll.
  // useNavCounts listens, and waits a moment so a bulk action asks once.
  if (options.method && options.method !== 'GET' && typeof window !== 'undefined') {
    try { window.dispatchEvent(new CustomEvent(NAV_COUNTS_CHANGED)); } catch { /* no window */ }
  }

  return res.json();
}

// The event a write announces; useNavCounts refreshes the sidebar badges on it.
export const NAV_COUNTS_CHANGED = 'av:nav-counts-changed';

export const api = {
  get: (path, params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    return request(path + qs);
  },
  // For endpoints that return a file rather than JSON. Fetched rather than
  // linked so a failure comes back as an error the caller can show, instead of
  // a browser tab containing an error payload.
  getBlob: async (path, params) => {
    const qs = params ? '?' + new URLSearchParams(params).toString() : '';
    const res = await fetch(`${BASE}/${path}${qs}`);
    if (!res.ok) {
      const errData = await errorFrom(res);
      const e = new Error(errData.error || `HTTP ${res.status}`);
      e.status = res.status;
      throw e;
    }
    return res.blob();
  },
  post: (path, body) => request(path, { method: 'POST', body }),
  put: (path, body) => request(path, { method: 'PUT', body }),
  patch: (path, body) => request(path, { method: 'PATCH', body }),
  delete: (path) => request(path, { method: 'DELETE' }),
};

export default api;
